import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Megaphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDuyurular = () => {
  const [duyurular, setDuyurular] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDuyuru, setEditingDuyuru] = useState(null);
  const [formData, setFormData] = useState({
    baslik: "",
    icerik: "",
    aktif: true,
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchDuyurular = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/duyurular`, {
        headers: getAuthHeader(),
      });
      setDuyurular(response.data);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Duyurular yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDuyurular();
  }, [fetchDuyurular]);

  const openAddDialog = () => {
    setEditingDuyuru(null);
    setFormData({
      baslik: "",
      icerik: "",
      aktif: true,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (duyuru) => {
    setEditingDuyuru(duyuru);
    setFormData({
      baslik: duyuru.baslik || "",
      icerik: duyuru.icerik || "",
      aktif: duyuru.aktif ?? true,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingDuyuru) {
        await axios.put(`${API}/admin/duyurular/${editingDuyuru.id}`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("Duyuru güncellendi");
      } else {
        await axios.post(`${API}/admin/duyurular`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("Duyuru eklendi");
      }
      setDialogOpen(false);
      fetchDuyurular();
    } catch (error) {
      console.error("Error saving announcement:", error);
      toast.error("Duyuru kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu duyuruyu silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/admin/duyurular/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Duyuru silindi");
      fetchDuyurular();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast.error("Duyuru silinirken hata oluştu");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-duyurular-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Megaphone className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Duyuru Yönetimi
            </h1>
            <p className="text-slate-500 text-xs lg:text-sm">{duyurular.length} duyuru listeleniyor</p>
          </div>
        </div>
        <Button onClick={openAddDialog} className="btn-primary text-sm lg:text-base" data-testid="add-duyuru-button">
          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="ml-1">Yeni Duyuru</span>
        </Button>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
        {duyurular.map((duyuru) => (
          <div 
            key={duyuru.id} 
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            data-testid={`duyuru-card-${duyuru.id}`}
          >
            <div className="p-4 lg:p-6">
              <div className="flex items-start justify-between mb-2 lg:mb-3">
                <h3 className="text-base lg:text-lg font-semibold text-slate-800 line-clamp-1">
                  {duyuru.baslik}
                </h3>
                <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${duyuru.aktif ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {duyuru.aktif ? "Aktif" : "Pasif"}
                </span>
              </div>
              <p className="text-slate-600 text-xs lg:text-sm line-clamp-3 mb-3 lg:mb-4">
                {duyuru.icerik}
              </p>
              <p className="text-slate-400 text-[10px] lg:text-xs">
                {formatDate(duyuru.tarih)}
              </p>
            </div>
            <div className="px-4 lg:px-6 py-3 lg:py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => openEditDialog(duyuru)}
                className="p-1.5 lg:p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                data-testid={`edit-duyuru-${duyuru.id}`}
              >
                <Pencil className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              </button>
              <button
                onClick={() => handleDelete(duyuru.id)}
                className="p-1.5 lg:p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                data-testid={`delete-duyuru-${duyuru.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {duyurular.length === 0 && (
        <div className="text-center py-8 lg:py-12 bg-white rounded-xl border border-slate-200">
          <Megaphone className="w-10 h-10 lg:w-12 lg:h-12 text-slate-300 mx-auto mb-3 lg:mb-4" />
          <p className="text-slate-500 text-sm lg:text-base">Henüz duyuru bulunmuyor</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingDuyuru ? "Duyuru Düzenle" : "Yeni Duyuru Ekle"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="baslik" className="text-sm">Başlık</Label>
              <Input
                id="baslik"
                value={formData.baslik}
                onChange={(e) => setFormData({ ...formData, baslik: e.target.value })}
                required
                className="text-sm"
                data-testid="input-baslik"
              />
            </div>
            <div>
              <Label htmlFor="icerik" className="text-sm">İçerik</Label>
              <Textarea
                id="icerik"
                value={formData.icerik}
                onChange={(e) => setFormData({ ...formData, icerik: e.target.value })}
                rows={4}
                required
                className="text-sm"
                data-testid="input-icerik"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="aktif"
                checked={formData.aktif}
                onCheckedChange={(checked) => setFormData({ ...formData, aktif: checked })}
                data-testid="switch-aktif"
              />
              <Label htmlFor="aktif" className="text-sm">Aktif</Label>
            </div>
            <div className="flex justify-end gap-2 lg:gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="text-sm"
                data-testid="cancel-button"
              >
                <X className="w-4 h-4 mr-1" />
                İptal
              </Button>
              <Button type="submit" className="btn-primary text-sm" data-testid="save-button">
                <Save className="w-4 h-4 mr-1" />
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDuyurular;
